/* eslint-disable max-lines */
import React from 'react';
import PropTypes from 'prop-types';
import serialize from 'form-serialize';
import deepmerge from 'deepmerge';
import { FormSaveButtons } from './FormSaveButtons';
import { FieldGroup } from './FieldGroup';
import BootstrapForm from 'react-bootstrap/Form';
import { ConfirmationModal } from '../ConfirmationModal';
import { CancelConfirmModal } from '../CancelConfirmModal';
import { AlertMessage } from '../AlertMessage';
import { AlertMessageTypes } from '../../constants/index';
import { i18n } from '../../i18n/translate';
import { JsonApiRecordDecorator } from '../../json-api/JsonApiRecordDecorator';
import _get from 'lodash/get';

export class Form extends React.Component {
  static propTypes = {
    activeLanguage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    addAction: PropTypes.func,
    buttonGroupClass: PropTypes.string,
    buttonStyle: PropTypes.string,
    buttonStyleDelete: PropTypes.string,
    buttonStyleSecondary: PropTypes.string,
    cancelButton: PropTypes.bool,
    cancelButtonText: PropTypes.string,
    cancelConfirmText: PropTypes.object,
    cancelIconType: PropTypes.string,
    clearMessages: PropTypes.func,
    children: PropTypes.any,
    container: PropTypes.node,
    data: PropTypes.object.isRequired,
    deepMerge: PropTypes.bool,
    deleteAction: PropTypes.func,
    deleteButton: PropTypes.bool,
    deleteButtonText: PropTypes.string,
    deleteDisableButtons: PropTypes.bool,
    deleteIconType: PropTypes.string,
    deleteModalTitle: PropTypes.string,
    deleteModalBody: PropTypes.string,
    deleteModalConfirmButtonText: PropTypes.string,
    deleteModalCancelButtonText: PropTypes.string,
    disableButtons: PropTypes.bool,
    errorMessage: PropTypes.string,
    fieldGroupClass: PropTypes.string,
    hasFormButtons: PropTypes.bool,
    languages: PropTypes.arrayOf(PropTypes.object),
    leftButtonStyle: PropTypes.string,
    leftButtonText: PropTypes.string,
    leftButtonClick: PropTypes.func,
    leftButtonIconType: PropTypes.string,
    noPaddingButtons: PropTypes.bool,
    onCancel: PropTypes.func,
    onCancelClose: PropTypes.func,
    onInvalidFormSubmit: PropTypes.func,
    saveAndAddButton: PropTypes.bool,
    saveAndAddButtonDisabled: PropTypes.bool,
    saveAndAddButtonText: PropTypes.string,
    saveAndAddCheckbox: PropTypes.bool,
    saveAndAddCheckboxText: PropTypes.string,
    saveAndAddIconType: PropTypes.string,
    saveButton: PropTypes.bool,
    saveButtonAreaClassName: PropTypes.string,
    saveButtonDisabled: PropTypes.bool,
    saveButtonText: PropTypes.string,
    saveButtonDataAction: PropTypes.string,
    saveComplete: PropTypes.func,
    saveIconType: PropTypes.string,
    scrollContainerId: PropTypes.string,
    showAlertMessages: PropTypes.bool,
    skipCancelConfirmation: PropTypes.bool,
    successMessage: PropTypes.string,
    triggerCancel: PropTypes.bool,
    triggerSave: PropTypes.bool,
    triggerSaveAndAdd: PropTypes.bool,
    updateAction: PropTypes.func,
  };

  static defaultProps = {
    cancelButton: true,
    deepMerge: false,
    deleteButton: false,
    deleteDisableButtons: true,
    deleteModalBody: '',
    hasFormButtons: true,
    showAlertMessages: true,
    saveAndAddButton: false,
    saveAndAddButtonDisabled: false,
    saveButton: true,
    saveButtonDisabled: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorMessage: props.errorMessage,
      isDisabled: props.disableButtons,
      deleteModal: false,
    };
  }

  async componentDidUpdate(prevProps) {
    if (
      (!this.props.disableButtons && prevProps.disableButtons) ||
      (this.props.successMessage && !prevProps.successMessage)
    ) {
      this.setState({ isDisabled: false });
    }
    if (this.props.errorMessage && this.props.errorMessage !== prevProps.errorMessage) {
      this.setState({ errorMessage: this.props.errorMessage, runValidation: true, isDisabled: false });
    }
    if (
      (this.props.triggerSave && !prevProps.triggerSave) ||
      (this.props.triggerSaveAndAdd && !prevProps.triggerSaveAndAdd)
    ) {
      try {
        const addAnother = this.props.triggerSaveAndAdd;
        const formValidated = await this.save(addAnother);
        if (this.props.saveComplete) {
          this.props.saveComplete(!!formValidated);
        }
      } catch (err) {
        this.setState({ errorMessage: err.message, runValidation: true, isDisabled: false });
        if (this.props.saveComplete) {
          this.props.saveComplete(false);
        }
      }
    }
    if (this.props.triggerCancel && !prevProps.triggerCancel && this.onCancel) {
      this.onCancel();
    }
  }

  toggleDeleteModal = () => {
    this.setState({ deleteModal: !this.state.deleteModal });
  };

  onValueChange = obj => {
    Object.assign(this.props.data, obj);
  };

  save = async addAnother => {
    const form = this.bootstrapForm;
    if (form.checkValidity()) {
      const currentData = this.serializeForm(form);
      if (currentData.id) {
        if (this.props.updateAction) {
          await this.props.updateAction(currentData);
        }
      } else {
        if (this.props.addAction) {
          await this.props.addAction(currentData, addAnother);
        }
      }
      this.setState({ isDisabled: false });
      return currentData;
    } else {
      this.setState({ errorMessage: i18n.t('Errors on the form!'), runValidation: true, isDisabled: false });

      if (typeof this.props.onInvalidFormSubmit === 'function') {
        this.props.onInvalidFormSubmit();
      }

      return false;
    }
  };

  saveAndAdd = e => {
    e.preventDefault();
    this.setState({ isDisabled: true });
    this.save(true);
  };

  saveAndClose = e => {
    e.preventDefault();
    this.setState({ isDisabled: true });
    this.save();
  };

  onCancel = () => {
    const form = this.bootstrapForm;
    const currentData = this.serializeForm(form);
    if (currentData.isDirty && !this.props.skipCancelConfirmation) {
      this.setState({ showCancelConfirm: true });

      return;
    }

    if (this.props.onCancel) {
      this.props.onCancel();
    }
  };

  normalizeStructures(source, stack = []) {
    const data = this.props.data;

    function getDataValue(key) {
      if (stack.length) {
        return _get(data, stack.concat(key));
      }

      return data[key];
    }

    Object.keys(source).forEach(key => {
      const wasArrayValue = Array.isArray(getDataValue(key));
      const currentValue = source[key];

      if (wasArrayValue && !Array.isArray(currentValue) && !!currentValue) {
        source[key] = [currentValue];
      } else if (Array.isArray(currentValue)) {
        source[key] = currentValue.filter(v => !!v);
      } else if (typeof currentValue === 'object') {
        this.normalizeStructures(currentValue, stack.concat(key));
      }
    });
  }

  serializeForm = form => {
    const obj = serialize(form, { hash: true, empty: true });
    // form-serialize does not serialize checkbox array correctly if only last item is checked
    this.normalizeStructures(obj);
    let newData = new JsonApiRecordDecorator();
    if (this.props.deepMerge) {
      const merged = deepmerge.all([newData, this.props.data, obj]);
      newData = Object.assign(newData, this.props.data, merged);
    } else {
      newData = Object.assign(newData, this.props.data, obj);
    }

    return newData;
  };

  onCancelClose = close => {
    this.setState({ showCancelConfirm: false });

    if (close && this.props.onCancel) {
      this.props.onCancel();

      return;
    }

    if (this.props.onCancelClose) {
      this.props.onCancelClose();
    }
  };

  deleteRecord = () => {
    if (this.props.deleteDisableButtons) {
      this.setState({ isDisabled: true });
    }
    this.props.deleteAction(this.props.data);
    this.toggleDeleteModal();
  };

  clearMessages = () => {
    const { clearMessages } = this.props;

    this.setState({
      errorMessage: null,
    });

    if (clearMessages) {
      clearMessages();
    }
  };

  render() {
    let saveForm = this.props.saveButton ? this.saveAndClose : null;
    // If data has id this is an update = don't show checkbox
    let saveAndAddCheckbox = false;
    if (this.props.saveAndAddCheckbox && !this.props.data.id) {
      saveForm = this.saveAndAdd;
      saveAndAddCheckbox = this.props.saveAndAddCheckbox;
    }
    const cancel = this.props.cancelButton ? this.onCancel : null;
    const saveAndAdd = this.props.saveAndAddButton ? this.saveAndAdd : null;
    const deleteButton = this.props.deleteButton ? this.deleteRecord : null;
    const deleteToggleButton = this.props.deleteButton ? this.toggleDeleteModal : null;
    const { deleteModalTitle, deleteModalConfirmButtonText, deleteModalCancelButtonText } = Object.assign(
      {
        deleteModalTitle: i18n.t('Are you sure?'),
        deleteModalConfirmButtonText: i18n.t('Yes'),
        deleteModalCancelButtonText: i18n.t('Cancel'),
      },
      this.props
    );

    return (
      <BootstrapForm ref={form => (this.bootstrapForm = form)}>
        <ConfirmationModal
          confirmButtonText={deleteModalConfirmButtonText}
          cancelButtonText={deleteModalCancelButtonText}
          canClickOff={true}
          onConfirm={deleteButton}
          onCancel={this.toggleDeleteModal}
          show={this.state.deleteModal}
          title={deleteModalTitle}
          container={this.props.container}
        >
          <div>{this.props.deleteModalBody}</div>
        </ConfirmationModal>
        <CancelConfirmModal
          {...this.props.cancelConfirmText}
          onCancelClose={this.onCancelClose}
          show={this.state.showCancelConfirm}
          container={this.props.container}
        />
        {this.props.showAlertMessages && this.state.errorMessage && (
          <AlertMessage
            clearMessages={this.clearMessages}
            message={this.state.errorMessage}
            messageType={AlertMessageTypes.danger}
            visible={true}
          />
        )}
        <FieldGroup
          activeLanguage={this.props.activeLanguage}
          className={this.props.fieldGroupClass}
          data={this.props.data}
          languages={this.props.languages}
          onValueChange={this.onValueChange}
          runValidation={this.state.runValidation}
          scrollContainerId={this.props.scrollContainerId}
        >
          {this.props.children}
        </FieldGroup>
        {this.props.hasFormButtons && (
          <FormSaveButtons
            buttonStyle={this.props.buttonStyle}
            buttonStyleDelete={this.props.buttonStyleDelete}
            buttonStyleSecondary={this.props.buttonStyleSecondary}
            cancel={cancel}
            cancelButtonText={this.props.cancelButtonText}
            cancelIconType={this.props.cancelIconType}
            className={this.props.buttonGroupClass}
            dataId={this.props.data && this.props.data.id ? this.props.data.id : ''}
            dataType={this.props.data && this.props.data.type ? this.props.data.type : ''}
            delete={deleteToggleButton}
            deleteButtonText={this.props.deleteButtonText}
            deleteIconType={this.props.deleteIconType}
            isDisabled={this.state.isDisabled}
            leftButtonStyle={this.props.leftButtonStyle}
            leftButtonText={this.props.leftButtonText}
            leftButtonClick={this.props.leftButtonClick}
            leftButtonIconType={this.props.leftButtonIconType}
            noPaddingButtons={this.props.noPaddingButtons}
            saveAndAddCheckbox={saveAndAddCheckbox}
            saveAndAddCheckboxText={this.props.saveAndAddCheckboxText}
            save={saveForm}
            saveAndAdd={saveAndAdd}
            saveAndAddButtonText={this.props.saveAndAddButtonText}
            saveAndAddButtonDisabled={this.props.saveAndAddButtonDisabled}
            saveAndAddIconType={this.props.saveAndAddIconType}
            saveButtonAreaClassName={this.props.saveButtonAreaClassName}
            saveButtonDisabled={this.props.saveButtonDisabled}
            saveButtonText={this.props.saveButtonText}
            saveButtonDataAction={this.props.saveButtonDataAction}
            saveIconType={this.props.saveIconType}
          />
        )}
      </BootstrapForm>
    );
  }
}
